import React, { useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { salesService } from '../services/saleService';
import { useAuth } from '../context/AuthContext';
import { PERMISSIONS } from '../constants/permissions';
import { SaleHistory } from '../types';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import CalendarModal from '../components/CalendarModal';
import { formatMoney } from '../utils/format';
import toast from 'react-hot-toast';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Search,
  DollarSign,
  User,
  Building2,
  ShoppingCart,
  ChevronRight,
  ChevronLeft,
  RefreshCw,
  Download,
  X,
  Banknote,
  Calendar,
  MapPin,
  TrendingUp,
  Receipt,
  AlertTriangle,
  Ban,
  ArrowRightLeft,
  Phone,
  Mail,
  ChevronDown,
  ChevronUp,
  Info,
} from 'lucide-react';

const safeNumber = (value: any): number => {
  if (value === null || value === undefined) return 0;
  const n = Number(value);
  return isNaN(n) ? 0 : n;
};

const PAYMENT_TYPE_CONFIG: Record<string, { label: string; color: string; bg: string; icon: React.ReactNode }> = {
  EFECTIVO: {
    label: 'Efectivo',
    color: 'text-success',
    bg: 'bg-success/10',
    icon: <Receipt className="w-3 h-3" />,
  },
  FINANCIADO: {
    label: 'Financiado',
    color: 'text-primary',
    bg: 'bg-primary/10',
    icon: <TrendingUp className="w-3 h-3" />,
  },
};

const ITEMS_PER_PAGE = 9;

const formatShortDate = (dateString: string) => {
  if (!dateString) return '—';
  return new Date(dateString).toLocaleDateString('es-AR', {
    day: '2-digit', month: 'short', year: 'numeric',
  });
};

const formatAmount = (amount: number, currency?: string) => {
  const symbol = currency?.toUpperCase() === 'USD' ? 'US$' : '$';
  return `${symbol} ${formatMoney(amount)}`;
};

const getPaymentTypeConfig = (type: string) =>
  PAYMENT_TYPE_CONFIG[type?.toUpperCase()] ?? {
    label: type || 'Otro',
    color: 'text-muted-foreground',
    bg: 'bg-muted',
    icon: <Receipt className="w-3 h-3" />,
  };

// ─── Tarjeta individual ───────────────────────────────────────────────────────

interface SaleCardProps {
  sale: SaleHistory;
  onViewDetail: (sale: SaleHistory) => void;
  onCancelled: () => void;
}

function SaleCard({ sale, onViewDetail, onCancelled }: SaleCardProps) {
  const { hasPermission } = useAuth();
  const canCancel = hasPermission(PERMISSIONS.COMERCIAL.VENTAS_CREAR);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const isCancelled = sale.status === 'CANCELLED';
  const isUSD = sale.currency?.toUpperCase() === 'USD';
  const customerLabel = sale.customer?.customerName || sale.customer?.email || 'Sin nombre';

  const handleCancel = async () => {
    setCancelling(true);
    try {
      await salesService.cancelSale(sale.id);
      toast.success('Venta cancelada correctamente');
      setConfirmOpen(false);
      onCancelled();
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'No se pudo cancelar la venta';
      toast.error(msg);
    } finally {
      setCancelling(false);
    }
  };

  return (
    <div
      className={`flex flex-col rounded-2xl border bg-card overflow-hidden transition-all duration-200 ${
        isCancelled
          ? 'border-destructive/25 opacity-75'
          : 'border-border hover:border-primary/30 hover:shadow-md'
      }`}
    >
      {/* ── Barra superior: ID + estado + moneda ── */}
      <div className={`flex items-center justify-between px-4 py-2.5 border-b ${
        isCancelled ? 'bg-destructive/5 border-destructive/15' : 'bg-muted/30 border-border/60'
      }`}>
        <div className="flex items-center gap-2">
          <span className="text-[11px] font-mono font-semibold text-muted-foreground">
            Venta #{sale.id}
          </span>
          {isCancelled ? (
            <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-destructive/15 text-destructive border border-destructive/20">
              <Ban className="w-2.5 h-2.5" />
              CANCELADA
            </span>
          ) : (
            <span className="inline-flex text-[10px] font-bold px-2 py-0.5 rounded-full bg-success/10 text-success border border-success/20">
              ACTIVA
            </span>
          )}
        </div>
        <span className={`text-[11px] font-bold px-2 py-0.5 rounded border tabular-nums ${
          isUSD
            ? 'bg-info/10 border-info/25 text-info'
            : 'bg-muted border-border text-muted-foreground'
        }`}>
          {sale.currency?.toUpperCase()}
        </span>
      </div>

      {/* ── Cliente + total ── */}
      <div className="flex items-start justify-between gap-3 px-4 pt-4 pb-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className={`shrink-0 w-10 h-10 rounded-xl flex items-center justify-center ${
            isCancelled ? 'bg-destructive/10' : 'bg-primary/10'
          }`}>
            {isCancelled
              ? <Ban className="w-5 h-5 text-destructive" />
              : <User className="w-5 h-5 text-primary" />
            }
          </div>
          <div className="min-w-0">
            <p className={`text-sm font-bold truncate leading-tight ${isCancelled ? 'text-muted-foreground' : 'text-foreground'}`}>
              {customerLabel}
            </p>
            {sale.customer?.cuit && (
              <p className="text-[11px] font-mono text-muted-foreground mt-0.5">
                {sale.customer.cuit}
              </p>
            )}
            {sale.customer?.customerType && (
              <span className={`inline-block text-[10px] font-medium px-1.5 py-0.5 rounded-full mt-1 ${
                sale.customer.customerType === 'PERSON'
                  ? 'bg-info/10 text-info'
                  : 'bg-primary/10 text-primary'
              }`}>
                {sale.customer.customerType === 'PERSON' ? 'Persona' : 'Empresa'}
              </span>
            )}
          </div>
        </div>

        <div className="text-right shrink-0">
          <p className={`text-lg font-bold tabular-nums leading-tight ${
            isCancelled ? 'text-muted-foreground line-through' : isUSD ? 'text-info' : 'text-amount'
          }`}>
            {formatAmount(safeNumber(sale.total), sale.currency)}
          </p>
          {safeNumber(sale.exchangeRate) > 0 && (
            <p className="text-[10px] text-muted-foreground mt-0.5 tabular-nums flex items-center gap-0.5 justify-end">
              <ArrowRightLeft className="w-2.5 h-2.5" />
              TC {safeNumber(sale.exchangeRate).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </p>
          )}
        </div>
      </div>

      {/* ── Unidades funcionales ── */}
      {(sale.functionalUnits?.length || 0) > 0 && (
        <div className="px-4 pb-3">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">
            Unidad{sale.functionalUnits.length > 1 ? 'es' : ''} Funcional{sale.functionalUnits.length > 1 ? 'es' : ''}
          </p>
          <div className="space-y-1.5">
            {sale.functionalUnits.slice(0, expanded ? undefined : 2).map((unit, idx) => (
              <div key={idx} className="flex items-center justify-between gap-2 px-2.5 py-2 rounded-lg bg-muted/40 border border-border/50">
                <div className="flex items-center gap-2 min-w-0">
                  <Building2 className="w-3.5 h-3.5 text-primary shrink-0" />
                  <div className="min-w-0">
                    <p className="text-xs font-medium text-foreground truncate leading-tight">{unit.projectName}</p>
                    <div className="flex items-center gap-1 mt-0.5 flex-wrap">
                      {unit.type && <span className="text-[10px] text-muted-foreground">{unit.type}</span>}
                      {unit.floor && <span className="text-[10px] text-muted-foreground">· Piso {unit.floor}</span>}
                      {unit.letter && <span className="text-[10px] font-semibold text-muted-foreground">{unit.letter}</span>}
                      {unit.location && (
                        <span className="flex items-center gap-0.5 text-[10px] text-muted-foreground">
                          <MapPin className="w-2.5 h-2.5" />{unit.location}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                {unit.priceAtSale != null && (
                  <p className="text-xs font-bold tabular-nums text-foreground shrink-0">
                    {formatAmount(safeNumber(unit.priceAtSale), sale.currency)}
                  </p>
                )}
              </div>
            ))}
            {!expanded && sale.functionalUnits.length > 2 && (
              <button
                onClick={() => setExpanded(true)}
                className="text-[11px] text-primary font-medium hover:underline pl-1"
              >
                + {sale.functionalUnits.length - 2} unidad{sale.functionalUnits.length - 2 > 1 ? 'es' : ''} más
              </button>
            )}
          </div>
        </div>
      )}

      {/* ── Métodos de pago ── */}
      {(sale.paymentMethods?.length || 0) > 0 && (
        <div className="px-4 pb-3">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">
            Método{sale.paymentMethods.length > 1 ? 's' : ''} de Pago
          </p>
          <div className="flex flex-col gap-1.5">
            {sale.paymentMethods.map((p, idx) => {
              const cfg = getPaymentTypeConfig(p.paymentType);
              const isFinancing = p.paymentType === 'FINANCIADO';
              return (
                <div key={idx} className="flex items-center justify-between gap-2 px-2.5 py-2 rounded-lg bg-muted/40 border border-border/50">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-md ${cfg.bg} ${cfg.color}`}>
                      {cfg.icon}
                      {cfg.label}
                    </span>
                    <span className="text-[10px] text-muted-foreground truncate">{p.description || ''}</span>
                    {p.accountingType && (
                      <span className="text-[10px] font-bold px-1.5 py-0.5 rounded border border-border/60 bg-muted text-muted-foreground shrink-0">
                        Tipo {p.accountingType}
                      </span>
                    )}
                    {isFinancing && p.installments && (
                      <span className="text-[10px] font-medium text-primary shrink-0">{p.installments} c.</span>
                    )}
                    {p.iva && safeNumber(p.iva) > 0 && (
                      <span className="text-[10px] text-muted-foreground shrink-0">+IVA</span>
                    )}
                  </div>
                  <p className={`text-xs font-bold tabular-nums shrink-0 ${isFinancing ? 'text-primary' : 'text-amount'}`}>
                    {formatAmount(safeNumber(p.totalAmount ?? p.amount), p.currency)}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Fechas ── */}
      <div className="px-4 pb-3 flex items-center gap-4">
        <div className="flex items-center gap-1.5">
          <Calendar className="w-3 h-3 text-muted-foreground" />
          <span className="text-[11px] text-muted-foreground">
            <span className="text-muted-foreground/70">Creación </span>
            {formatShortDate(sale.createdAt)}
          </span>
        </div>
        {sale.deliveryDate && (
          <>
            <div className="w-px h-3 bg-border" />
            <div className="flex items-center gap-1.5">
              <Calendar className="w-3 h-3 text-muted-foreground" />
              <span className="text-[11px] text-muted-foreground">
                <span className="text-muted-foreground/70">Entrega </span>
                {formatShortDate(sale.deliveryDate)}
              </span>
            </div>
          </>
        )}
      </div>

      {/* ── Zona de confirmación de cancelación ── */}
      {confirmOpen && (
        <div className="mx-4 mb-3 p-3 rounded-xl border border-destructive/30 bg-destructive/5 space-y-2.5">
          <div className="flex items-center gap-1.5">
            <AlertTriangle className="w-3.5 h-3.5 text-destructive shrink-0" />
            <p className="text-xs font-semibold text-destructive">¿Cancelar esta venta?</p>
          </div>
          <p className="text-[11px] text-muted-foreground leading-relaxed">
            Se liberarán las UFs y se eliminarán los pagos pendientes.{' '}
            <strong>No se puede deshacer.</strong>
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              className="flex-1 h-7 text-xs"
              onClick={() => setConfirmOpen(false)}
              disabled={cancelling}
            >
              Volver
            </Button>
            <Button
              size="sm"
              className="flex-1 h-7 text-xs bg-destructive hover:bg-destructive/90 text-white"
              onClick={handleCancel}
              disabled={cancelling}
            >
              {cancelling
                ? <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
                : <Ban className="w-3 h-3 mr-1" />
              }
              {cancelling ? 'Cancelando…' : 'Confirmar'}
            </Button>
          </div>
        </div>
      )}

      {/* ── Nota sobre cancelación (colapsable) ── */}
      {expanded && (
        <div className="mx-4 mb-3 flex gap-2 p-2.5 rounded-lg border border-amber-500/20 bg-amber-500/5">
          <Info className="w-3.5 h-3.5 text-amber-500 shrink-0 mt-0.5" />
          <p className="text-[10px] text-muted-foreground leading-relaxed">
            Solo se puede cancelar si <strong>no hay cobranzas realizadas</strong>. Con al menos una cobranza registrada, la venta no puede revertirse.
          </p>
        </div>
      )}

      {/* ── Acciones de la card ── */}
      <div className={`mt-auto border-t px-4 py-2.5 flex items-center justify-between gap-2 ${
        isCancelled ? 'border-destructive/15 bg-destructive/5' : 'border-border bg-muted/20'
      }`}>
        <button
          onClick={() => setExpanded(v => !v)}
          className="flex items-center gap-1 text-[11px] font-medium text-muted-foreground hover:text-foreground transition-colors"
        >
          {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          {expanded ? 'Menos' : 'Más info'}
        </button>

        <div className="flex items-center gap-2">
          {canCancel && !isCancelled && !confirmOpen && (
            <button
              onClick={() => setConfirmOpen(true)}
              className="flex items-center gap-1 text-[11px] font-medium text-destructive/70 hover:text-destructive transition-colors"
            >
              <Ban className="w-3 h-3" />
              Cancelar
            </button>
          )}
          <button
            onClick={() => onViewDetail(sale)}
            className="flex items-center gap-1 text-[11px] font-semibold text-primary hover:underline transition-colors"
          >
            Ver detalle
            <ChevronRight className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Página principal ─────────────────────────────────────────────────────────

export default function SalesHistory() {
  const [sales, setSales] = useState<SaleHistory[]>([]);
  const [filteredSales, setFilteredSales] = useState<SaleHistory[]>([]);
  const [loading, setLoading] = useState(true);

  const [searchTerm, setSearchTerm] = useState('');
  const [currencyFilter, setCurrencyFilter] = useState('ALL');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [dateFilter, setDateFilter] = useState({ startDate: '', endDate: '' });
  const [sortColumn, setSortColumn] = useState<'date' | 'total' | 'customer'>('date');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  const [page, setPage] = useState(1);

  const [selectedSale, setSelectedSale] = useState<SaleHistory | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  const [desdeModalOpen, setDesdeModalOpen] = useState(false);
  const [hastaModalOpen, setHastaModalOpen] = useState(false);

  const [stats, setStats] = useState({ active: 0, cancelled: 0, totalARS: 0, totalUSD: 0 });

  const fetchSales = useCallback(async () => {
    try {
      setLoading(true);
      const data = await salesService.getAllSales();
      setSales(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching sales:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const applyFilters = useCallback(() => {
    let filtered = [...sales];

    if (statusFilter !== 'ALL') {
      filtered = filtered.filter((s) => s.status === statusFilter);
    }

    if (currencyFilter !== 'ALL') {
      filtered = filtered.filter(
        (s) => s.currency?.toUpperCase() === currencyFilter.toUpperCase()
      );
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (s) =>
          s.id?.toString().includes(term) ||
          s.customer?.customerName?.toLowerCase().includes(term) ||
          s.customer?.cuit?.toLowerCase().includes(term) ||
          s.customer?.email?.toLowerCase().includes(term) ||
          s.observations?.toLowerCase().includes(term) ||
          s.functionalUnits?.some((u) => u.projectName?.toLowerCase().includes(term))
      );
    }

    if (dateFilter.startDate) {
      filtered = filtered.filter(
        (s) => new Date(s.createdAt) >= new Date(dateFilter.startDate + 'T00:00:00')
      );
    }
    if (dateFilter.endDate) {
      filtered = filtered.filter(
        (s) => new Date(s.createdAt) <= new Date(dateFilter.endDate + 'T23:59:59')
      );
    }

    filtered.sort((a, b) => {
      let comparison = 0;
      switch (sortColumn) {
        case 'date':
          comparison = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
          break;
        case 'total':
          comparison = safeNumber(a.total) - safeNumber(b.total);
          break;
        case 'customer':
          comparison = (a.customer?.customerName || a.customer?.email || '').localeCompare(
            b.customer?.customerName || b.customer?.email || ''
          );
          break;
      }
      return sortDirection === 'asc' ? comparison : -comparison;
    });

    setFilteredSales(filtered);
    setPage(1);
  }, [sales, currencyFilter, statusFilter, searchTerm, dateFilter, sortColumn, sortDirection]);

  const calculateStats = useCallback(() => {
    let totalARS = 0;
    let totalUSD = 0;
    let active = 0;
    let cancelled = 0;
    sales.forEach((s) => {
      if (s.status === 'CANCELLED') { cancelled++; return; }
      active++;
      const amount = safeNumber(s.total);
      if (s.currency?.toUpperCase() === 'USD') totalUSD += amount;
      else totalARS += amount;
    });
    setStats({ active, cancelled, totalARS, totalUSD });
  }, [sales]);

  useEffect(() => { fetchSales(); }, [fetchSales]);
  useEffect(() => { applyFilters(); }, [applyFilters]);
  useEffect(() => { calculateStats(); }, [calculateStats]);

  const openDetails = (sale: SaleHistory) => { setSelectedSale(sale); setDetailsOpen(true); };
  const closeDetails = () => { setDetailsOpen(false); setSelectedSale(null); };

  const exportToCSV = () => {
    const headers = ['ID', 'Estado', 'Cliente', 'CUIT', 'Fecha Creación', 'Fecha Entrega', 'Moneda', 'Total', 'Tipo Cambio', 'Unidades Funcionales', 'Métodos de Pago', 'Observaciones'];
    const rows = filteredSales.map((s) => [
      s.id,
      s.status,
      `"${(s.customer?.customerName || '').replace(/"/g, '""')}"`,
      s.customer?.cuit || '',
      formatShortDate(s.createdAt),
      formatShortDate(s.deliveryDate),
      s.currency,
      safeNumber(s.total).toFixed(2),
      safeNumber(s.exchangeRate).toFixed(4),
      s.functionalUnits?.length || 0,
      s.paymentMethods?.length || 0,
      `"${(s.observations || '').replace(/"/g, '""')}"`,
    ]);
    const csvContent = [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `historial_ventas_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setCurrencyFilter('ALL');
    setStatusFilter('ALL');
    setDateFilter({ startDate: '', endDate: '' });
  };

  const hasActiveFilters = searchTerm || currencyFilter !== 'ALL' || statusFilter !== 'ALL' || dateFilter.startDate || dateFilter.endDate;

  const totalPages = Math.ceil(filteredSales.length / ITEMS_PER_PAGE);
  const safePage = Math.min(page, Math.max(1, totalPages));
  const pageItems = filteredSales.slice((safePage - 1) * ITEMS_PER_PAGE, safePage * ITEMS_PER_PAGE);

  return (
    <div className="w-full h-full p-6 space-y-6">
      <div className="overflow-hidden border rounded-xl shadow-sm bg-card border-border">

        {/* ── Header ── */}
        <div className="px-6 py-5 border-b border-border bg-gradient-to-r from-card to-muted/30">
          <div className="flex flex-col items-start justify-between gap-4 mb-5 sm:flex-row sm:items-center">
            <div className="flex items-center gap-4">
              <div className="p-2.5 rounded-xl bg-primary/10">
                <ShoppingCart className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-foreground">Historial de Ventas</h1>
                <p className="mt-0.5 text-sm text-muted-foreground">Registro completo de ventas realizadas</p>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-success/10 text-success text-xs font-semibold">
                <ShoppingCart className="w-3.5 h-3.5" />
                {stats.active} activa{stats.active !== 1 ? 's' : ''}
              </span>
              {stats.cancelled > 0 && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-destructive/10 text-destructive text-xs font-semibold">
                  <Ban className="w-3.5 h-3.5" />
                  {stats.cancelled} cancelada{stats.cancelled !== 1 ? 's' : ''}
                </span>
              )}
              {stats.totalARS > 0 && (
                <span className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amount/10 text-amount text-xs font-semibold">
                  <DollarSign className="w-3.5 h-3.5" />
                  {formatAmount(stats.totalARS, 'ARS')}
                </span>
              )}
              {stats.totalUSD > 0 && (
                <span className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-info/10 text-info text-xs font-semibold">
                  <Banknote className="w-3.5 h-3.5" />
                  {formatAmount(stats.totalUSD, 'USD')}
                </span>
              )}
              <Button variant="outline" size="sm" onClick={fetchSales} disabled={loading}>
                <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Actualizar
              </Button>
              <Button size="sm" onClick={exportToCSV} disabled={filteredSales.length === 0}>
                <Download className="w-4 h-4 mr-2" />
                Exportar CSV
              </Button>
            </div>
          </div>

          {/* Filtros */}
          <div className="flex flex-wrap items-end gap-3">
            {/* Estado */}
            <div className="space-y-1.5">
              <label className="text-xs font-medium uppercase text-muted-foreground">Estado</label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[150px] rounded-lg h-9">
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">Todos</SelectItem>
                  <SelectItem value="ACTIVE">Activas</SelectItem>
                  <SelectItem value="CANCELLED">Canceladas</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Moneda */}
            <div className="space-y-1.5">
              <label className="text-xs font-medium uppercase text-muted-foreground">Moneda</label>
              <Select value={currencyFilter} onValueChange={setCurrencyFilter}>
                <SelectTrigger className="w-[150px] rounded-lg h-9">
                  <SelectValue placeholder="Todas" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">Todas</SelectItem>
                  <SelectItem value="ARS">ARS — Pesos</SelectItem>
                  <SelectItem value="USD">USD — Dólares</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Desde */}
            <div className="space-y-1.5">
              <label className="text-xs font-medium uppercase text-muted-foreground">Desde</label>
              <div className="flex gap-1">
                <Button variant="outline" className="justify-start w-[148px] font-normal text-left rounded-lg h-9" onClick={() => setDesdeModalOpen(true)}>
                  <Calendar className="w-3.5 h-3.5 mr-2 shrink-0 text-muted-foreground" />
                  <span className="truncate text-xs">{dateFilter.startDate ? formatShortDate(dateFilter.startDate) : 'Seleccionar'}</span>
                </Button>
                {dateFilter.startDate && (
                  <Button variant="ghost" size="icon" className="h-9 w-9 shrink-0" onClick={() => setDateFilter(d => ({ ...d, startDate: '' }))}>
                    <X className="w-3.5 h-3.5" />
                  </Button>
                )}
              </div>
            </div>

            {/* Hasta */}
            <div className="space-y-1.5">
              <label className="text-xs font-medium uppercase text-muted-foreground">Hasta</label>
              <div className="flex gap-1">
                <Button variant="outline" className="justify-start w-[148px] font-normal text-left rounded-lg h-9" onClick={() => setHastaModalOpen(true)}>
                  <Calendar className="w-3.5 h-3.5 mr-2 shrink-0 text-muted-foreground" />
                  <span className="truncate text-xs">{dateFilter.endDate ? formatShortDate(dateFilter.endDate) : 'Seleccionar'}</span>
                </Button>
                {dateFilter.endDate && (
                  <Button variant="ghost" size="icon" className="h-9 w-9 shrink-0" onClick={() => setDateFilter(d => ({ ...d, endDate: '' }))}>
                    <X className="w-3.5 h-3.5" />
                  </Button>
                )}
              </div>
            </div>

            {hasActiveFilters && (
              <div className="space-y-1.5">
                <label className="text-xs font-medium uppercase text-transparent select-none">·</label>
                <Button variant="ghost" size="sm" onClick={clearFilters} className="h-9 flex items-center gap-1.5 text-muted-foreground hover:text-foreground rounded-lg">
                  <X className="w-3.5 h-3.5" />
                  Limpiar
                </Button>
              </div>
            )}

            {/* Búsqueda al final */}
            <div className="space-y-1.5 ml-auto">
              <label className="text-xs font-medium uppercase text-muted-foreground">Buscar</label>
              <div className="relative">
                <Search className="absolute w-3.5 h-3.5 transform -translate-y-1/2 left-3 top-1/2 text-muted-foreground" />
                <Input
                  placeholder="Cliente, CUIT, proyecto, ID…"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="rounded-lg pl-9 h-9 w-72 focus-visible:ring-0 focus-visible:ring-offset-0"
                />
              </div>
            </div>
          </div>
        </div>

        {/* ── Cuerpo ── */}
        <div className="p-6">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-24">
              <RefreshCw className="w-8 h-8 mb-3 animate-spin text-muted-foreground" />
              <p className="text-sm text-muted-foreground">Cargando ventas…</p>
            </div>
          ) : filteredSales.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-24 text-center">
              <div className="mx-auto mb-5 w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center">
                <ShoppingCart className="w-8 h-8 text-primary" />
              </div>
              <h3 className="mb-2 text-lg font-semibold text-foreground">No hay ventas</h3>
              <p className="mb-6 text-sm text-muted-foreground max-w-xs mx-auto">
                {hasActiveFilters
                  ? 'No se encontraron ventas con los filtros aplicados.'
                  : 'Aún no se registran ventas en el sistema.'}
              </p>
              {hasActiveFilters && (
                <Button variant="outline" size="sm" onClick={clearFilters}>Limpiar filtros</Button>
              )}
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
                {pageItems.map((sale) => (
                  <SaleCard
                    key={sale.id}
                    sale={sale}
                    onViewDetail={openDetails}
                    onCancelled={fetchSales}
                  />
                ))}
              </div>

              <div className="flex items-center justify-between pt-4 mt-4 border-t border-border">
                <p className="text-sm text-muted-foreground">
                  Mostrando <span className="font-semibold text-foreground">{pageItems.length}</span> de{' '}
                  <span className="font-semibold text-foreground">{filteredSales.length}</span> venta{filteredSales.length !== 1 ? 's' : ''}
                </p>
                {totalPages > 1 && (
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={() => setPage(p => p - 1)} disabled={safePage === 1}>
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <span className="text-sm text-foreground">{safePage} / {totalPages}</span>
                    <Button variant="outline" size="sm" onClick={() => setPage(p => p + 1)} disabled={safePage === totalPages}>
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* ── Modal detalle completo ── */}
      {detailsOpen && selectedSale && createPortal(
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
          <div className="absolute inset-0 bg-background/80 backdrop-blur-sm" onClick={closeDetails} />
          <div className="relative z-10 w-full sm:max-w-2xl max-h-[92dvh] sm:max-h-[88dvh] flex flex-col bg-background border border-border sm:rounded-2xl rounded-t-2xl shadow-2xl overflow-hidden">

            {/* Header */}
            <div className="shrink-0 px-5 pt-5 pb-4 border-b border-border">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-mono font-medium text-muted-foreground">Venta #{selectedSale.id}</span>
                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded border ${
                      selectedSale.currency?.toUpperCase() === 'USD'
                        ? 'border-info/30 text-info bg-info/10'
                        : 'border-border text-muted-foreground bg-muted'
                    }`}>
                      {selectedSale.currency?.toUpperCase()}
                    </span>
                    {selectedSale.status === 'CANCELLED' && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded border border-destructive/30 text-destructive bg-destructive/10">
                        <Ban className="w-2.5 h-2.5" />
                        CANCELADA
                      </span>
                    )}
                  </div>
                  <h2 className="text-xl font-bold text-foreground truncate">
                    {selectedSale.customer?.customerName || 'Sin cliente'}
                  </h2>
                  <p className="text-xs text-muted-foreground mt-0.5 font-mono">
                    {selectedSale.customer?.cuit}
                  </p>
                </div>
                <div className="flex items-start gap-2 shrink-0">
                  <div className="text-right">
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Total</p>
                    <p className={`text-2xl font-bold tabular-nums ${selectedSale.status === 'CANCELLED' ? 'text-muted-foreground line-through' : 'text-foreground'}`}>
                      {formatAmount(safeNumber(selectedSale.total), selectedSale.currency)}
                    </p>
                  </div>
                  <button onClick={closeDetails} className="mt-0.5 p-1.5 rounded-lg hover:bg-muted transition-colors">
                    <X className="w-4 h-4 text-muted-foreground" />
                  </button>
                </div>
              </div>

              <div className="flex items-center gap-4 mt-3 pt-3 border-t border-border/60 flex-wrap">
                <div>
                  <p className="text-[10px] text-muted-foreground uppercase">Creación</p>
                  <p className="text-xs font-medium text-foreground mt-0.5">{formatShortDate(selectedSale.createdAt)}</p>
                </div>
                <div className="w-px h-6 bg-border" />
                <div>
                  <p className="text-[10px] text-muted-foreground uppercase">Entrega</p>
                  <p className="text-xs font-medium text-foreground mt-0.5">{formatShortDate(selectedSale.deliveryDate)}</p>
                </div>
                <div className="w-px h-6 bg-border" />
                <div>
                  <p className="text-[10px] text-muted-foreground uppercase">T. Cambio</p>
                  <p className="text-xs font-medium text-foreground mt-0.5 tabular-nums">
                    {safeNumber(selectedSale.exchangeRate) > 0 ? safeNumber(selectedSale.exchangeRate).toLocaleString('es-AR', { minimumFractionDigits: 2 }) : '—'}
                  </p>
                </div>
                {selectedSale.customer?.customerType && (
                  <>
                    <div className="w-px h-6 bg-border" />
                    <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full border ${
                      selectedSale.customer.customerType === 'PERSON'
                        ? 'bg-info/10 border-info/20 text-info'
                        : 'bg-primary/10 border-primary/20 text-primary'
                    }`}>
                      {selectedSale.customer.customerType === 'PERSON' ? 'Persona' : 'Empresa'}
                    </span>
                  </>
                )}
              </div>
            </div>

            {/* Contenido scrollable */}
            <div className="flex-1 overflow-y-auto scrollbar-custom divide-y divide-border/60">

              {/* Cliente */}
              <div className="px-5 py-4">
                <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-3">Cliente</p>
                <div className="grid grid-cols-2 gap-x-6 gap-y-2.5">
                  <div>
                    <p className="text-[11px] text-muted-foreground">Nombre</p>
                    <p className="text-sm font-medium text-foreground mt-0.5">{selectedSale.customer?.customerName || '—'}</p>
                  </div>
                  <div>
                    <p className="text-[11px] text-muted-foreground">CUIT</p>
                    <p className="text-sm font-mono text-foreground mt-0.5">{selectedSale.customer?.cuit || '—'}</p>
                  </div>
                  {selectedSale.customer?.email && (
                    <div>
                      <p className="text-[11px] text-muted-foreground">Email</p>
                      <p className="text-sm text-foreground mt-0.5 truncate flex items-center gap-1">
                        <Mail className="w-3 h-3 text-muted-foreground shrink-0" />
                        {selectedSale.customer.email}
                      </p>
                    </div>
                  )}
                  {selectedSale.customer?.phone && (
                    <div>
                      <p className="text-[11px] text-muted-foreground">Teléfono</p>
                      <p className="text-sm text-foreground mt-0.5 flex items-center gap-1">
                        <Phone className="w-3 h-3 text-muted-foreground shrink-0" />
                        {selectedSale.customer.phone}
                      </p>
                    </div>
                  )}
                  {selectedSale.customer?.address && (
                    <div className="col-span-2">
                      <p className="text-[11px] text-muted-foreground">Dirección</p>
                      <p className="text-sm text-foreground mt-0.5">{selectedSale.customer.address}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* UFs */}
              {(selectedSale.functionalUnits?.length || 0) > 0 && (
                <div className="px-5 py-4">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">Unidades Funcionales</p>
                    <span className="text-[11px] text-muted-foreground">{selectedSale.functionalUnits.length} unidad{selectedSale.functionalUnits.length !== 1 ? 'es' : ''}</span>
                  </div>
                  <div className="space-y-2">
                    {selectedSale.functionalUnits.map((unit, idx) => (
                      <div key={idx} className="flex items-center justify-between gap-3 py-2.5 px-3 rounded-xl bg-muted/40">
                        <div className="flex items-center gap-2.5 min-w-0">
                          <div className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                            <Building2 className="w-3.5 h-3.5 text-primary" />
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-foreground truncate">{unit.projectName}</p>
                            <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                              {unit.type && <span className="text-xs text-muted-foreground">{unit.type}</span>}
                              {unit.floor && <span className="text-xs text-muted-foreground">· Piso {unit.floor}</span>}
                              {unit.letter && <span className="text-xs text-muted-foreground">{unit.letter}</span>}
                              {unit.location && (
                                <span className="flex items-center gap-0.5 text-xs text-muted-foreground">
                                  <MapPin className="w-2.5 h-2.5" />{unit.location}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        {unit.priceAtSale != null && (
                          <p className="text-sm font-semibold text-foreground tabular-nums shrink-0">
                            {formatAmount(safeNumber(unit.priceAtSale), selectedSale.currency)}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Métodos de pago */}
              {(selectedSale.paymentMethods?.length || 0) > 0 && (
                <div className="px-5 py-4">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">Métodos de Pago</p>
                    <span className="text-[11px] text-muted-foreground">{selectedSale.paymentMethods.length} método{selectedSale.paymentMethods.length !== 1 ? 's' : ''}</span>
                  </div>
                  <div className="space-y-2">
                    {selectedSale.paymentMethods.map((payment, idx) => {
                      const cfg = getPaymentTypeConfig(payment.paymentType);
                      const isFinancing = payment.paymentType === 'FINANCIADO';
                      return (
                        <div key={idx} className="rounded-xl bg-muted/40 overflow-hidden">
                          <div className="flex items-center justify-between gap-3 px-3 py-2.5">
                            <div className="flex items-center gap-2.5 min-w-0">
                              <div className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${isFinancing ? 'bg-primary/10' : 'bg-success/10'}`}>
                                {isFinancing ? <TrendingUp className="w-3.5 h-3.5 text-primary" /> : <Receipt className="w-3.5 h-3.5 text-success" />}
                              </div>
                              <div className="min-w-0">
                                <p className="text-sm font-medium text-foreground truncate">{payment.description || `Método ${idx + 1}`}</p>
                                <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                                  <span className={`text-[11px] font-medium px-1.5 py-0.5 rounded-md ${cfg.bg} ${cfg.color}`}>{cfg.label}</span>
                                  <span className="text-[11px] text-muted-foreground">{payment.currency} · Tipo {payment.accountingType}</span>
                                  {payment.iva && <span className="text-[11px] text-muted-foreground">· IVA {payment.iva}%</span>}
                                </div>
                              </div>
                            </div>
                            <p className={`text-sm font-bold tabular-nums shrink-0 ${isFinancing ? 'text-primary' : 'text-amount'}`}>
                              {formatAmount(safeNumber(payment.totalAmount ?? payment.amount), payment.currency)}
                            </p>
                          </div>
                          {isFinancing && (
                            <div className="grid grid-cols-3 gap-2 px-3 pb-3 pt-1 border-t border-border/40">
                              {payment.installments != null && (
                                <div>
                                  <p className="text-[10px] text-muted-foreground">Cuotas</p>
                                  <p className="text-xs font-semibold text-foreground mt-0.5">{payment.installments}</p>
                                </div>
                              )}
                              {payment.firstInstallmentDate && (
                                <div>
                                  <p className="text-[10px] text-muted-foreground">1ra Cuota</p>
                                  <p className="text-xs font-medium text-foreground mt-0.5">{formatShortDate(payment.firstInstallmentDate)}</p>
                                </div>
                              )}
                              {payment.financingType && (
                                <div>
                                  <p className="text-[10px] text-muted-foreground">Financiación</p>
                                  <p className="text-xs font-medium text-foreground mt-0.5">{payment.financingType}</p>
                                </div>
                              )}
                              {payment.indexationType && (
                                <div>
                                  <p className="text-[10px] text-muted-foreground">Indexación</p>
                                  <p className="text-xs font-medium text-foreground mt-0.5">{payment.indexationType}</p>
                                </div>
                              )}
                              {payment.exchangeRate && safeNumber(payment.exchangeRate) > 0 && (
                                <div>
                                  <p className="text-[10px] text-muted-foreground">T. Cambio</p>
                                  <p className="text-xs font-medium text-foreground mt-0.5 tabular-nums">{safeNumber(payment.exchangeRate).toLocaleString('es-AR', { minimumFractionDigits: 2 })}</p>
                                </div>
                              )}
                              {payment.moraRate && (
                                <div>
                                  <p className="text-[10px] text-muted-foreground">Mora</p>
                                  <p className="text-xs font-medium text-foreground mt-0.5">
                                    {payment.moraRate}%{' '}
                                    {payment.moraFrequency === 'DAILY' ? 'diaria' : payment.moraFrequency === 'MONTHLY' ? 'mensual' : 'anual'}
                                  </p>
                                </div>
                              )}
                            </div>
                          )}
                          {!isFinancing && (payment.exchangeRate || payment.paymentDate) && (
                            <div className="grid grid-cols-3 gap-2 px-3 pb-3 pt-1 border-t border-border/40">
                              {payment.exchangeRate && safeNumber(payment.exchangeRate) > 0 && (
                                <div>
                                  <p className="text-[10px] text-muted-foreground">T. Cambio</p>
                                  <p className="text-xs font-medium text-foreground mt-0.5 tabular-nums">{safeNumber(payment.exchangeRate).toLocaleString('es-AR', { minimumFractionDigits: 2 })}</p>
                                </div>
                              )}
                              {payment.paymentDate && (
                                <div>
                                  <p className="text-[10px] text-muted-foreground">F. Pago</p>
                                  <p className="text-xs font-medium text-foreground mt-0.5">{formatShortDate(payment.paymentDate)}</p>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <div className="flex items-center justify-between mt-3 pt-3 border-t border-border/60">
                    <p className="text-xs font-medium text-muted-foreground">Total métodos de pago</p>
                    <p className="text-sm font-bold text-primary tabular-nums">
                      {formatAmount(
                        selectedSale.paymentMethods.reduce((sum, p) => sum + safeNumber(p.totalAmount ?? p.amount), 0),
                        selectedSale.currency
                      )}
                    </p>
                  </div>
                </div>
              )}

              {/* Observaciones */}
              {selectedSale.observations && (
                <div className="px-5 py-4">
                  <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-2">Observaciones</p>
                  <p className="text-sm text-foreground leading-relaxed">{selectedSale.observations}</p>
                </div>
              )}

              {/* Nota cancelación */}
              <div className="px-5 py-4">
                <div className={`flex gap-3 p-3.5 rounded-xl border ${selectedSale.status === 'CANCELLED' ? 'bg-destructive/5 border-destructive/20' : 'bg-amber-500/5 border-amber-500/20'}`}>
                  <AlertTriangle className={`w-4 h-4 mt-0.5 shrink-0 ${selectedSale.status === 'CANCELLED' ? 'text-destructive' : 'text-amber-500'}`} />
                  <div className="space-y-1">
                    {selectedSale.status === 'CANCELLED'
                      ? <p className="text-xs font-semibold text-destructive">Esta venta fue cancelada</p>
                      : <p className="text-xs font-semibold text-amber-600 dark:text-amber-400">Sobre la cancelación</p>
                    }
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      Solo se puede cancelar una venta si <strong>no tiene cobranzas realizadas</strong>. Al hacerlo, se liberan las
                      unidades funcionales y se eliminan los pagos pendientes. Una vez registrada una cobranza,
                      la venta <strong>no puede revertirse</strong>.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      , document.body)}

      <CalendarModal
        open={desdeModalOpen}
        onOpenChange={setDesdeModalOpen}
        onSelectDate={(date) => setDateFilter({ ...dateFilter, startDate: date.toISOString().split('T')[0] })}
        selectedDate={dateFilter.startDate ? new Date(dateFilter.startDate) : undefined}
      />
      <CalendarModal
        open={hastaModalOpen}
        onOpenChange={setHastaModalOpen}
        onSelectDate={(date) => setDateFilter({ ...dateFilter, endDate: date.toISOString().split('T')[0] })}
        selectedDate={dateFilter.endDate ? new Date(dateFilter.endDate) : undefined}
      />
    </div>
  );
}
